import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DateInput from "@/components/DateInput";
import { toast } from "sonner";
import { Bank, PencilSimple } from "@phosphor-icons/react";

const todayISO = () => new Date().toISOString().slice(0, 10);

export function DepositarChequeDialog({ cheque, onClose, onDone }) {
  const [cajas, setCajas] = useState([]);
  const [cajaId, setCajaId] = useState("");
  const [fecha, setFecha] = useState(todayISO());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!cheque) return;
    setCajaId(""); setFecha(todayISO());
    api.get("/cajas").then(({ data }) =>
      setCajas((data || []).filter((c) => !["cheques", "cheques_emitidos"].includes(c.tipo) && (c.moneda || "ARS") === (cheque.moneda || "ARS")))
    );
  }, [cheque]);

  const depositar = async () => {
    if (!cajaId) return toast.error("Elegí la caja/banco destino");
    setSaving(true);
    try {
      await api.post(`/cheques/${cheque.id}/depositar`, { caja_destino_id: cajaId, fecha });
      toast.success("Cheque depositado: se transfirió el importe a la caja elegida");
      onDone?.(); onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo depositar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!cheque} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-md rounded-sm" data-testid="depositar-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Bank size={18} className="text-[#6B1F24]" /> Depositar cheque</DialogTitle>
        </DialogHeader>
        {cheque && (
          <div className="text-[12px] font-mono bg-neutral-50 border rounded-sm px-3 py-2">
            Nº {cheque.numero} · {cheque.banco_nombre} · {cheque.cliente_nombre}
            <div className="font-semibold mt-0.5">$ {Number(cheque.monto || 0).toLocaleString("es-AR", { minimumFractionDigits: 2 })}</div>
          </div>
        )}
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] uppercase font-mono text-neutral-500">Depositar en</Label>
            <Select value={cajaId} onValueChange={setCajaId}>
              <SelectTrigger className="h-9 rounded-sm" data-testid="depositar-caja"><SelectValue placeholder="Elegí banco / caja" /></SelectTrigger>
              <SelectContent>
                {cajas.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre} <span className="text-[10px] text-neutral-500 ml-1">({c.tipo})</span></SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase font-mono text-neutral-500">Fecha de depósito</Label>
            <DateInput value={fecha} onChange={(e) => setFecha(e.target.value)} className="h-9 border rounded-sm px-2 text-[13px] w-full" data-testid="depositar-fecha" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancelar</Button>
          <Button onClick={depositar} disabled={saving || !cajaId} className="rounded-sm bg-[#6B1F24] hover:bg-[#571519]" data-testid="depositar-confirmar">
            {saving ? "Depositando…" : "Depositar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EditarChequeDialog({ cheque, onClose, onDone }) {
  const [bancos, setBancos] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!cheque) return;
    setForm({
      numero: cheque.numero?.startsWith("S/N-") ? "" : cheque.numero || "",
      banco_code: cheque.banco_code || "",
      fecha_emision: cheque.fecha_emision || "",
      fecha_cobro: cheque.fecha_cobro || "",
      firmante: cheque.firmante || "",
      cuit_emisor: cheque.cuit_emisor || "",
    });
    api.get("/cheques/bancos").then(({ data }) => setBancos(data || [])).catch(() => {});
  }, [cheque]);

  const guardar = async () => {
    if (!form.numero?.trim()) return toast.error("Ingresá el número de cheque");
    const banco = bancos.find((b) => String(b.code) === String(form.banco_code));
    setSaving(true);
    try {
      await api.put(`/cheques/${cheque.id}`, {
        ...form,
        numero: form.numero.trim(),
        banco_code: form.banco_code || undefined,
        banco_nombre: banco ? banco.name : undefined,
        firmante: form.firmante || undefined,
        cuit_emisor: form.cuit_emisor || undefined,
      });
      toast.success("Cheque actualizado");
      onDone?.(); onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!cheque} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-lg rounded-sm" data-testid="editar-cheque-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><PencilSimple size={18} className="text-[#6B1F24]" /> Datos del cheque</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-[11px] uppercase font-mono text-neutral-500">Nº de cheque</Label>
            <Input value={form.numero || ""} onChange={(e) => setForm({ ...form, numero: e.target.value })} className="h-9 rounded-sm" data-testid="editar-numero" />
          </div>
          <div className="col-span-2">
            <Label className="text-[11px] uppercase font-mono text-neutral-500">Banco</Label>
            <Select value={form.banco_code ? String(form.banco_code) : ""} onValueChange={(v) => setForm({ ...form, banco_code: v })}>
              <SelectTrigger className="h-9 rounded-sm" data-testid="editar-banco"><SelectValue placeholder="Elegí el banco" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {bancos.map((b) => <SelectItem key={b.code} value={String(b.code)}>{b.code} · {b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase font-mono text-neutral-500">F. emisión</Label>
            <DateInput value={form.fecha_emision || ""} onChange={(e) => setForm({ ...form, fecha_emision: e.target.value })} className="h-9 border rounded-sm px-2 text-[13px] w-full" data-testid="editar-emision" />
          </div>
          <div>
            <Label className="text-[11px] uppercase font-mono text-neutral-500">F. cobro</Label>
            <DateInput value={form.fecha_cobro || ""} onChange={(e) => setForm({ ...form, fecha_cobro: e.target.value })} className="h-9 border rounded-sm px-2 text-[13px] w-full" data-testid="editar-cobro" />
          </div>
          <div>
            <Label className="text-[11px] uppercase font-mono text-neutral-500">Firmante</Label>
            <Input value={form.firmante || ""} onChange={(e) => setForm({ ...form, firmante: e.target.value })} className="h-9 rounded-sm" />
          </div>
          <div>
            <Label className="text-[11px] uppercase font-mono text-neutral-500">CUIT emisor</Label>
            <Input value={form.cuit_emisor || ""} onChange={(e) => setForm({ ...form, cuit_emisor: e.target.value })} className="h-9 rounded-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancelar</Button>
          <Button onClick={guardar} disabled={saving} className="rounded-sm bg-[#6B1F24] hover:bg-[#571519]" data-testid="editar-guardar">{saving ? "Guardando…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
